import {queries} from '@/shared/integrations/zero'
import {ListTradeNamesInput} from '@cypro/engine/queries'
import {TradeName} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type TradeNameStartRow = Pick<TradeName, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteTradeNames({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<ListTradeNamesInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.types, filters?.forms, filters?.substances, filters?.search, limit, search]
  )

  const getPageQuery: GetPageQuery<TradeName, TradeNameStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
            }
          : undefined

      return {
        query: queries.products.listTradeNames({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, ...(search ? {search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  const getSingleQuery: GetSingleQuery<TradeName> = useCallback(
    ({id}) => ({
      query: queries.products.getTradeNameById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: TradeName): TradeNameStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: TradeName) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
